import React, { ReactNode, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Link } from 'react-router-dom';
import LogoLink from '../../components/top-nav/logo-link';
import userIconUrl from '../../assets/svg/user-nav.svg';
import { getLoginBackground } from './helpers';
import './login.scss';

const APP_BAR_HEIGHT = '68px';
const BACKGROUND_FALLBACK = '#E6E3DF';
const MODAL_HEIGHT = '650px';
const MODAL_VIEWPORT_GUTTER = '64px';
const loadedBackgroundUrls = new Set<string>();

export interface LoginLayoutProps {
  children: ReactNode;
}

const LoginLayout = ({ children }: LoginLayoutProps): React.ReactElement => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { placeholder, full } = getLoginBackground(isDesktop ? 'desktop' : 'mobile');
  const [loaded, setLoaded] = useState(() => loadedBackgroundUrls.has(full));

  useEffect(() => {
    if (loadedBackgroundUrls.has(full)) {
      setLoaded(true);
      return undefined;
    }

    setLoaded(false);
    const img = new Image();
    img.onload = () => {
      loadedBackgroundUrls.add(full);
      setLoaded(true);
    };
    img.src = full;
    return () => {
      img.onload = null;
    };
  }, [full]);

  const layerSx = {
    position: 'absolute' as const,
    inset: 0,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  };

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        width: '100%',
        overflow: 'hidden',
        backgroundColor: BACKGROUND_FALLBACK,
      }}
    >
      <Box
        aria-hidden
        sx={{
          ...layerSx,
          backgroundImage: `url(${placeholder})`,
          filter: 'blur(20px)',
          transform: 'scale(1.08)',
          zIndex: 0,
        }}
      />
      <Box
        aria-hidden
        sx={{
          ...layerSx,
          backgroundImage: `url(${full})`,
          opacity: loaded ? 1 : 0,
          transition: 'opacity 1s ease-in-out',
          zIndex: 0,
        }}
      />

      <Box
        component="nav"
        sx={{
          position: 'relative',
          zIndex: 2,
          height: APP_BAR_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 2, md: 4 },
          backgroundColor: 'rgba(255, 255, 255, 0.92)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
        }}
      >
        <LogoLink onClick={() => {}} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box
            component="a"
            href="tel:8884412418"
            sx={{
              display: { xs: 'none', sm: 'block' },
              fontSize: '14px',
              letterSpacing: '0.56px',
              textTransform: 'uppercase',
              color: '#333',
              textDecoration: 'none',
            }}
          >
            888 441-2418
          </Box>
          <Tooltip title="Sign In">
            <Box
              component={Link}
              to="/login"
              aria-label="Sign In"
              sx={{
                display: 'inline-flex',
                lineHeight: 0,
              }}
            >
              <Box
                component="img"
                src={userIconUrl}
                alt=""
                aria-hidden
                sx={{ height: '1.5rem', width: '1.5rem' }}
              />
            </Box>
          </Tooltip>
        </Box>
      </Box>

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          minHeight: `calc(100vh - ${APP_BAR_HEIGHT})`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: { xs: 3, md: 4 },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: { xs: '100%', md: 480 },
            maxWidth: 480,
            height: `min(${MODAL_HEIGHT}, calc(100vh - ${APP_BAR_HEIGHT} - ${MODAL_VIEWPORT_GUTTER}))`,
            maxHeight: `calc(100vh - ${APP_BAR_HEIGHT} - ${MODAL_VIEWPORT_GUTTER})`,
            overflowY: 'auto',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 0,
            backgroundColor: '#FAF6F2',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.18)',
            '& .MuiButton-root': {
              borderRadius: 0,
            },
          }}
        >
          <Box
            sx={{
              flex: '1 0 auto',
              boxSizing: 'border-box',
              width: '100%',
              minHeight: '100%',
              display: 'flex',
              flexDirection: 'column',
              pt: '24px',
              px: '24px',
              pb: '48px',
            }}
          >
            {children}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default LoginLayout;
